// customCursorPixels — pure RGBA byte-buffer helpers for the 24×24 custom
// cursor editor / thumbnail renderers (custom-cursor-2026-09-19.md §2.2/§2.5).
//
// Kept dependency-free of React/DOM so it's testable under the web package's
// default node-env vitest config (`vitest.config.ts` — no DOM). The one DOM
// touchpoint (drawing to a `<canvas>`) lives in `customCursorDraw.ts`
// (fillRect-loop, same idiom as `drawArgbCanvas.ts` — see that file's header
// for why this codebase avoids `ImageData`/`putImageData` in web: jsdom has
// no Canvas 2D backend, so pixel-level canvas ops are untestable at the
// component level here and the convention is to keep them as fillRect
// blocks that a fake ctx can record).
//
// `CustomCursor.rgba` is a base64 string decoding to exactly
// `CURSOR_RGBA_BYTES` (2304) bytes, row-major RGBA, non-premultiplied
// (protocol/src/shared/cursor.ts). `atob`/`btoa` are used for the codec —
// both are standard globals in browsers and Node 18+ (the runtime this
// package targets), so no extra dependency is needed.

import { CURSOR_RGBA_BYTES, CURSOR_SIZE, type CustomCursor } from '@oekaki/protocol';

export { CURSOR_RGBA_BYTES, CURSOR_SIZE };

/** A single RGBA pixel, channels 0..255. */
export interface CursorRgba {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
}

/** Fully transparent pixel — the value OOB reads / a fresh editor return. */
const TRANSPARENT: CursorRgba = { r: 0, g: 0, b: 0, a: 0 };

/** A brand-new, fully transparent 24×24 buffer (= editor's blank starting state). */
export const createEmptyCursorPixels = (): Uint8ClampedArray =>
  new Uint8ClampedArray(CURSOR_RGBA_BYTES);

/** Defensive clamp/pad to exactly `CURSOR_RGBA_BYTES`, used by `encodeCursorPixels`. */
const normalizeLength = (pixels: Uint8ClampedArray): Uint8ClampedArray => {
  if (pixels.length === CURSOR_RGBA_BYTES) return pixels;
  const out = new Uint8ClampedArray(CURSOR_RGBA_BYTES);
  out.set(pixels.subarray(0, Math.min(pixels.length, CURSOR_RGBA_BYTES)));
  return out;
};

/**
 * Decode a wire `CustomCursor` into a 2304-byte pixel buffer. The schema
 * already guarantees `rgba` decodes to exactly `CURSOR_RGBA_BYTES` (fixed
 * base64 length + charset, see `protocol/src/shared/cursor.ts`), but this
 * still defensively normalizes length in case a caller hands in a
 * hand-built (non-schema-validated) value.
 */
export const decodeCursorPixels = (cursor: CustomCursor): Uint8ClampedArray => {
  const binary = atob(cursor.rgba);
  const out = new Uint8ClampedArray(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return normalizeLength(out);
};

/** Encode a pixel buffer back into the wire `CustomCursor` shape. */
export const encodeCursorPixels = (pixels: Uint8ClampedArray): CustomCursor => {
  const bytes = normalizeLength(pixels);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] as number);
  }
  return { rgba: btoa(binary) };
};

/** Immutable-style copy (matches the reducer-style helpers in `RoomSession.ts`). */
export const cloneCursorPixels = (pixels: Uint8ClampedArray): Uint8ClampedArray =>
  Uint8ClampedArray.from(pixels);

const inBounds = (x: number, y: number): boolean =>
  Number.isInteger(x) &&
  Number.isInteger(y) &&
  x >= 0 &&
  x < CURSOR_SIZE &&
  y >= 0 &&
  y < CURSOR_SIZE;

/** Read a single pixel. OOB coordinates return fully transparent (never throws). */
export const getCursorPixel = (pixels: Uint8ClampedArray, x: number, y: number): CursorRgba => {
  if (!inBounds(x, y)) return TRANSPARENT;
  const i = (y * CURSOR_SIZE + x) * 4;
  return {
    r: pixels[i] ?? 0,
    g: pixels[i + 1] ?? 0,
    b: pixels[i + 2] ?? 0,
    a: pixels[i + 3] ?? 0,
  };
};

/**
 * Write a single pixel, returning a NEW buffer (reducer-style, mirrors
 * `RoomSession.ts`'s `upsertMember` etc. — cheap here too, 2304 bytes).
 * OOB coordinates are a no-op (returns the SAME reference so callers can
 * cheaply skip a re-render/redraw via reference equality).
 */
export const setCursorPixel = (
  pixels: Uint8ClampedArray,
  x: number,
  y: number,
  color: CursorRgba,
): Uint8ClampedArray => {
  if (!inBounds(x, y)) return pixels;
  const next = cloneCursorPixels(pixels);
  const i = (y * CURSOR_SIZE + x) * 4;
  next[i] = color.r;
  next[i + 1] = color.g;
  next[i + 2] = color.b;
  next[i + 3] = color.a;
  return next;
};

/** Whole-buffer reset (= editor's "全消し"). Always a fresh transparent buffer. */
export const clearAllCursorPixels = (): Uint8ClampedArray => createEmptyCursorPixels();

/**
 * Map a pointer position (px relative to the grid's top-left, CSS px) to a
 * cell index, given the on-screen size of one cell (`cellPx`). Returns
 * `null` outside the 24×24 grid (caller should no-op).
 */
export const cellFromPoint = (
  px: number,
  py: number,
  cellPx: number,
): { readonly x: number; readonly y: number } | null => {
  if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(cellPx) || cellPx <= 0) {
    return null;
  }
  const x = Math.floor(px / cellPx);
  const y = Math.floor(py / cellPx);
  if (x < 0 || x >= CURSOR_SIZE || y < 0 || y >= CURSOR_SIZE) return null;
  return { x, y };
};

/** Whether a pixel buffer is entirely transparent (= "no cursor drawn yet"). */
export const isCursorPixelsBlank = (pixels: Uint8ClampedArray): boolean => {
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] !== 0) return false;
  }
  return true;
};
